<!-- 学生宠物成长详情弹窗：等级进度、学期分曲线、每日状态日历、领取更换记录。创建于 2026-10-07 -->
<template>
  <div>
    <el-dialog
    title="学生宠物成长详情"
    :visible="visible"
    width="960px"
    top="3vh"
    :close-on-click-modal="false"
    :show-close="true"
    append-to-body
    custom-class="pet-detail-dialog"
    @close="$emit('update:visible', false)"
    @opened="handleResize">
    <div v-loading="loading" class="detail">
      <header class="detail-head">
        <div class="detail-head__who">
          <oss-media-thumbs
            v-if="detail.headPhotoImage"
            class="who-photo"
            :media="detail.headPhotoImage"
            thumb-width="44px"
            thumb-height="44px" />
          <span v-else class="who-initial">{{ (detail.studentName || '?').slice(-1) }}</span>
          <div class="who-text">
            <span class="who-name">{{ detail.studentName || '—' }}</span>
            <span class="who-sub">{{ detail.studentNo || '—' }} · {{ detail.departmentName || '未分班' }}</span>
          </div>
        </div>
        <div class="detail-head__tools">
          <el-select v-model="innerSemesterId" size="small" class="semester-select" @change="handleSemesterChange">
            <el-option v-for="s in semesters" :key="s.semesterId" :value="s.semesterId" :label="s.semesterName + (s.isCurrent ? '（当前）' : '')" />
          </el-select>
          <el-date-picker
            v-model="innerViewDate"
            type="date"
            size="small"
            value-format="yyyy-MM-dd"
            :clearable="false"
            :picker-options="datePickerOptions"
            class="view-date"
            @change="load" />
        </div>
      </header>

      <div class="detail-body">
        <div class="overview">
        <section class="hero">
          <div class="hero__pet">
            <oss-media-thumbs
              v-if="detail.hasPet && detail.imageUrl"
              :media="detail.imageUrl"
              thumb-width="120px"
              thumb-height="120px" />
            <div v-else class="hero__pet-empty">
              <i class="el-icon-picture-outline"></i>
              <span>{{ detail.hasPet ? '该等级未上传形象图' : '尚未领取宠物' }}</span>
            </div>
          </div>
          <div class="hero__info">
            <div class="hero__title">
              <span class="pet-name">{{ detail.hasPet ? (detail.petName || '已删除宠物') : '未领取' }}</span>
              <span v-if="detail.nickname" class="pet-nickname">「{{ detail.nickname }}」</span>
              <span :class="['mood-tag', 'mood-' + moodKey(detail.mood)]">{{ detail.moodText || '平稳' }}</span>
            </div>
            <div class="hero__level">
              <span class="level-badge">Lv.{{ detail.levelIndex || 1 }}</span>
              <span class="level-name">{{ detail.levelName }}</span>
              <span class="muted">/ 共 {{ detail.maxLevel || 0 }} 级</span>
            </div>
            <div class="progress">
              <div class="progress__bar"><span :style="{ width: (detail.progressPercent || 0) + '%' }"></span></div>
              <span class="progress__text">
                <template v-if="detail.nextLevelName">
                  距「{{ detail.nextLevelName }}」还差 <b>{{ formatScore(gapToNext) }}</b> 分
                </template>
                <template v-else>已满级</template>
              </span>
            </div>
            <div class="hero__meta">
              <span>学期分 <b>{{ formatScore(detail.semesterScore) }}</b><em v-if="detail.scoreMode === 'PositiveOnly'">（只算加分）</em></span>
              <span>{{ detail.viewDate || '—' }} 得分 <b :class="scoreClass(detail.todayScore)">{{ signedScore(detail.todayScore) }}</b></span>
              <span v-if="detail.claimTime">领取于 {{ formatTime(detail.claimTime).slice(0, 10) }}</span>
            </div>
          </div>
        </section>

        <section class="metrics">
          <div class="metric"><span class="metric__label">有评价天数</span><span class="metric__value">{{ detail.activeDays || 0 }}</span></div>
          <div class="metric metric--success"><span class="metric__label">活力天数</span><span class="metric__value">{{ detail.energeticDays || 0 }}</span></div>
          <div class="metric metric--warning"><span class="metric__label">调整天数</span><span class="metric__value">{{ detail.adjustingDays || 0 }}</span></div>
          <div class="metric metric--violet">
            <span class="metric__label">家长更换</span>
            <span class="metric__value">{{ detail.usedChangeCount || 0 }}<small> / {{ detail.maxChangePerSemester < 0 ? '不限' : detail.maxChangePerSemester }}</small></span>
          </div>
        </section>
        </div>

        <section class="panel">
          <div class="panel__head">
            <span class="panel__title">学期分走势</span>
            <span class="chart-summary">
              <span>当前 <b>{{ formatScore(chart.lastScore) }}</b></span>
              <span>峰值 <b>{{ formatScore(chart.maxScore) }}</b></span>
              <span class="muted">{{ detail.startDate ? String(detail.startDate).slice(0, 10) : '' }} 至 {{ detail.viewDate || '' }}</span>
            </span>
          </div>
          <div v-if="!days.length" class="panel__empty">该学期暂无数据</div>
          <div v-else ref="scoreChart" class="chart"></div>
        </section>

        <section class="panel">
          <div class="panel__head">
            <span class="panel__title">每日状态</span>
            <span class="legend">
              <i class="legend__dot cell--energetic"></i>活力
              <i class="legend__dot cell--stable"></i>平稳（有评价）
              <i class="legend__dot cell--adjusting"></i>调整
              <i class="legend__dot cell--none"></i>无评价
            </span>
          </div>
          <div v-if="!months.length" class="panel__empty">该学期暂无数据</div>
          <div v-else class="months">
            <div v-for="month in months" :key="month.key" class="month">
              <div class="month__title">{{ month.title }}</div>
              <div class="month__week"><span v-for="w in weekNames" :key="w">{{ w }}</span></div>
              <div class="month__grid">
                <span v-for="n in month.offset" :key="'b' + n" class="cell cell--blank"></span>
                <span
                  v-for="day in month.days"
                  :key="day.date"
                  :class="['cell', cellClass(day)]"
                  :title="cellTitle(day)"
                  @click="openDayDetail(day)">{{ Number(day.date.slice(8)) }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="panel">
          <div class="panel__head"><span class="panel__title">领取 / 更换记录</span></div>
          <div v-if="!(detail.changeLogs || []).length" class="panel__empty">暂无记录</div>
          <el-timeline v-else class="logs">
            <el-timeline-item
              v-for="log in detail.changeLogs"
              :key="log.id"
              :timestamp="formatTime(log.createTime)"
              :color="log.actionType === 1 ? '#16a34a' : '#2563eb'"
              placement="top">
              <div class="log">
                <span :class="['log__type', log.actionType === 1 ? 'is-claim' : 'is-change']">{{ log.actionType === 1 ? '领取' : '更换' }}</span>
                <span v-if="log.fromPetName" class="log__pet">{{ log.fromPetName }} <i class="el-icon-right"></i></span>
                <span class="log__pet log__pet--to">{{ log.toPetName || '已删除宠物' }}</span>
                <span class="muted">{{ log.operatorType === 'admin' ? '管理员' : log.operatorType === 'classTeacher' ? '班主任' : '家长' }}{{ log.operatorName ? ' · ' + log.operatorName : '' }}</span>
                <span v-if="log.semesterName" class="muted">{{ log.semesterName }}</span>
              </div>
            </el-timeline-item>
          </el-timeline>
        </section>
      </div>
    </div>
    </el-dialog>

    <el-dialog
    title="每日状态明细"
    :visible.sync="dayVisible"
    width="420px"
    append-to-body>
    <div v-if="selectedDay" class="day-detail">
      <div class="day-detail__date">{{ selectedDay.date }}</div>
      <div class="day-detail__status">
        <span :class="['mood-tag', 'mood-' + cellClass(selectedDay).replace('cell--', '')]">
          {{ dayMoodText(selectedDay) }}
        </span>
        <span class="muted">{{ selectedDay.recordCount || 0 }} 条评价</span>
      </div>
      <div class="day-detail__grid">
        <div><span>当天净分</span><b :class="scoreClass(selectedDay.dayScore)">{{ signedScore(selectedDay.dayScore) }}</b></div>
        <div><span>计入学期分</span><b>{{ formatScore(selectedDay.countedScore) }}</b></div>
        <div><span>累计学期分</span><b>{{ formatScore(selectedDay.cumulativeScore) }}</b></div>
        <div><span>当日等级</span><b>Lv.{{ selectedDay.levelIndex || 1 }}</b></div>
      </div>
    </div>
    </el-dialog>
  </div>
</template>

<script>
import echarts from 'echarts'
import { getPetStudentDetail } from '@/api/modules/pet'

/** 解析 yyyy-MM-dd 为本地日期，避免时区偏移 */
function parseDay (value) {
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}

export default {
  name: 'StudentPetDetailDrawer',
  props: {
    visible: { type: Boolean, default: false },
    schoolId: { type: Number, default: 0 },
    studentId: { type: Number, default: 0 },
    semesterId: { type: Number, default: null },
    semesters: { type: Array, default: () => [] },
    levels: { type: Array, default: () => [] }
  },
  data () {
    return {
      loading: false,
      innerSemesterId: null,
      innerViewDate: null,
      detail: {},
      dayVisible: false,
      selectedDay: null,
      scoreChart: null,
      weekNames: ['一', '二', '三', '四', '五', '六', '日']
    }
  },
  computed: {
    days () {
      return this.detail.days || []
    },
    gapToNext () {
      const next = Number(this.detail.nextLevelMinScore) || 0
      return Math.max(0, next - Math.max(0, Number(this.detail.semesterScore) || 0))
    },
    datePickerOptions () {
      const start = String(this.detail.startDate || '').slice(0, 10)
      const end = String(this.detail.endDate || '').slice(0, 10)
      const today = new Date()
      const todayText = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      return {
        disabledDate: date => {
          const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
          return value > todayText || (start && value < start) || (end && value > end)
        }
      }
    },
    /** 学期分折线：累计分 + 等级门槛虚线 */
    chart () {
      const width = 680
      const height = 180
      const pad = 8
      const scores = this.days.map(x => Number(x.cumulativeScore) || 0)
      if (!scores.length) return { width, height, points: '', area: '', levelLines: [] }
      const dataMin = Math.min(...scores)
      const dataMax = Math.max(...scores)
      const padding = Math.max(1, (dataMax - dataMin) * .12)
      const min = dataMin - padding
      const max = dataMax + padding
      const range = max - min || 1
      const toY = v => pad + (height - pad * 2) * (1 - (v - min) / range)
      const step = scores.length > 1 ? width / (scores.length - 1) : 0
      const coords = scores.map((v, i) => [i * step, toY(v)])
      const points = coords.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
      const pointItems = this.days.map((day, i) => ({
        date: day.date,
        dayScore: day.dayScore,
        cumulativeScore: day.cumulativeScore,
        levelIndex: day.levelIndex,
        x: coords[i][0],
        y: coords[i][1]
      }))
      const gridLines = [0, 0.5, 1].map((ratio, i) => ({
        key: i,
        y: pad + (height - pad * 2) * ratio,
        value: this.formatScore(max - range * ratio)
      }))
      const baseY = toY(Math.min(max, Math.max(min, 0))).toFixed(1)
      const area = `M0,${baseY} ` + coords.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join(' ') + ` L${coords[coords.length - 1][0].toFixed(1)},${baseY} Z`
      const levelLines = this.levels
        .filter(l => Number(l.minScore) > 0 && Number(l.minScore) <= max)
        .map(l => ({ levelIndex: l.levelIndex, minScore: l.minScore, y: toY(Number(l.minScore)) }))
      return {
        width,
        height,
        points,
        area,
        levelLines,
        pointItems,
        gridLines,
        lastScore: scores[scores.length - 1],
        maxScore: Math.max(...scores)
      }
    },
    /** 按月分组的日历，offset 为该月首日前的空格数（周一开头） */
    months () {
      const groups = []
      this.days.forEach(day => {
        const key = day.date.slice(0, 7)
        let group = groups[groups.length - 1]
        if (!group || group.key !== key) {
          const first = parseDay(day.date)
          group = { key, title: `${first.getFullYear()} 年 ${first.getMonth() + 1} 月`, offset: (first.getDay() + 6) % 7, days: [] }
          groups.push(group)
        }
        group.days.push(day)
      })
      return groups
    }
  },
  watch: {
    visible (val) {
      if (val) {
        this.innerSemesterId = this.semesterId
        this.innerViewDate = null
        this.detail = {}
        this.load()
      }
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    if (this.scoreChart) {
      this.scoreChart.dispose()
      this.scoreChart = null
    }
  },
  methods: {
    renderScoreChart () {
      if (!this.$refs.scoreChart || !this.days.length) return
      if (!this.scoreChart) this.scoreChart = echarts.init(this.$refs.scoreChart)
      const days = this.days
      const scores = days.map(day => Number(day.cumulativeScore) || 0)
      this.scoreChart.setOption({
        grid: { left: 42, right: 20, top: 18, bottom: 28 },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, .96)',
          borderColor: '#e2e8f0',
          borderWidth: 1,
          padding: [8, 12],
          textStyle: { color: '#334155', fontSize: 12 },
          extraCssText: 'box-shadow: 0 6px 16px rgba(15, 23, 42, .12); border-radius: 8px;',
          axisPointer: { type: 'line', lineStyle: { color: '#93c5fd', type: 'dashed' } },
          formatter: params => {
            const item = (params || [])[0] || {}
            const day = days[item.dataIndex] || {}
            return `${day.date}<br/>当天净分：${this.signedScore(day.dayScore)} 分<br/>累计学期分：${this.formatScore(day.cumulativeScore)} 分<br/>等级：Lv.${day.levelIndex || 1}`
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: days.map(day => day.date),
          axisLabel: {
            textStyle: { color: '#94a3b8', fontSize: 11 },
            interval: Math.max(0, Math.ceil(days.length / 6) - 1),
            formatter: value => String(value).slice(5, 10)
          },
          axisLine: { lineStyle: { color: '#e5e7eb' } },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          min: value => Math.floor(value.min - 1),
          max: value => Math.ceil(value.max + 1),
          axisLabel: { textStyle: { color: '#94a3b8', fontSize: 11 } },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#eef2f7', type: 'dashed' } }
        },
        series: [{
          type: 'line',
          data: scores,
          smooth: 0.3,
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: false,
          lineStyle: { normal: { color: '#2563eb', width: 2.5 } },
          itemStyle: { normal: { color: '#2563eb', borderColor: '#fff', borderWidth: 2 } },
          areaStyle: {
            normal: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(37, 99, 235, .22)' },
                { offset: 1, color: 'rgba(37, 99, 235, .02)' }
              ])
            }
          }
        }]
      }, true)
    },
    handleResize () {
      if (this.scoreChart) this.scoreChart.resize()
    },
    handleSemesterChange () {
      this.innerViewDate = null
      this.load()
    },
    async load () {
      if (!this.schoolId || !this.studentId) return
      this.loading = true
      try {
        const res = await getPetStudentDetail({
          schoolId: this.schoolId,
          studentId: this.studentId,
          semesterId: this.innerSemesterId || null,
          viewDate: this.innerViewDate || null
        })
        if (res.data && res.data.success) {
          const detail = res.data.response || {}
          await this.prefetchListOssMedia([detail], ['headPhotoImage', 'imageUrl'])
          this.detail = detail
          this.innerViewDate = detail.viewDate || this.innerViewDate
          this.$nextTick(() => this.renderScoreChart())
        } else {
          this.$message.error((res.data && res.data.msg) || '加载失败')
        }
      } finally {
        this.loading = false
      }
    },
    cellClass (day) {
      if (day.moodCode === 1) return 'cell--energetic'
      if (day.moodCode === 2) return 'cell--adjusting'
      return day.recordCount > 0 ? 'cell--stable' : 'cell--none'
    },
    cellTitle (day) {
      const mood = day.moodCode === 1 ? '活力' : day.moodCode === 2 ? '调整' : '平稳'
      return `${day.date}  ${mood}\n当天净分 ${this.signedScore(day.dayScore)}（${day.recordCount} 条评价）\n累计学期分 ${this.formatScore(day.cumulativeScore)} · Lv.${day.levelIndex}`
    },
    dayMoodText (day) {
      if (day.moodCode === 1) return '活力'
      if (day.moodCode === 2) return '调整'
      return day.recordCount > 0 ? '平稳' : '无评价'
    },
    openDayDetail (day) {
      this.selectedDay = day
      this.dayVisible = true
    },
    moodKey (mood) {
      const value = String(mood || '').toLowerCase()
      if (value === 'energetic') return 'energetic'
      if (value === 'adjusting') return 'adjusting'
      return 'stable'
    },
    formatScore (value) {
      const num = Number(value) || 0
      return Number.isInteger(num) ? num : num.toFixed(1)
    },
    signedScore (value) {
      const num = Number(value) || 0
      return num > 0 ? `+${this.formatScore(num)}` : `${this.formatScore(num)}`
    },
    scoreClass (value) {
      const num = Number(value) || 0
      return num > 0 ? 'is-up' : num < 0 ? 'is-down' : ''
    },
    formatTime (value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').slice(0, 16)
    }
  }
}
</script>

<style>
.el-dialog.pet-detail-dialog > .el-dialog__body { max-height: none !important; overflow: hidden !important; padding: 0 !important; }
</style>

<style scoped>
.detail { display: flex; flex-direction: column; height: calc(88vh - 60px); background: #f8fafc; }
.detail-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 20px; background: #fff; border-bottom: 1px solid #e2e8f0; }
.detail-head__who { display: flex; align-items: center; gap: 12px; min-width: 0; }
.who-photo /deep/ img { border-radius: 8px; object-fit: cover; }
.who-initial { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 8px; background: #eff6ff; border: 1px solid #bfdbfe; color: #1d4ed8; font-size: 16px; font-weight: 600; }
.who-text { display: flex; flex-direction: column; min-width: 0; }
.who-name { font-size: 16px; font-weight: 600; color: #0f172a; }
.who-sub { font-size: 12px; color: #64748b; }
.detail-head__tools { display: flex; align-items: center; gap: 8px; }
.semester-select { width: 200px; }
.view-date { width: 140px !important; }
.detail-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 16px 20px 24px; display: flex; flex-direction: column; gap: 14px; }

.overview { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 14px; align-items: stretch; }
.hero { display: flex; gap: 18px; padding: 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; min-width: 0; }
.hero__pet { flex: 0 0 120px; height: 120px; display: flex; align-items: center; justify-content: center; border-radius: 10px; background: linear-gradient(160deg, #eff6ff, #f8fafc); overflow: hidden; }
.hero__pet /deep/ img { object-fit: contain; }
.hero__pet-empty { display: flex; flex-direction: column; align-items: center; gap: 6px; color: #94a3b8; font-size: 12px; text-align: center; padding: 0 8px; }
.hero__pet-empty i { font-size: 28px; }
.hero__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 10px; }
.hero__title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pet-name { font-size: 18px; font-weight: 600; color: #0f172a; }
.pet-nickname { color: #475569; font-size: 13px; }
.hero__level { display: flex; align-items: center; gap: 6px; }
.level-badge { display: inline-block; padding: 1px 8px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 12px; font-weight: 600; }
.level-name { font-size: 13px; color: #0f172a; font-weight: 500; }
.progress { display: flex; align-items: center; gap: 10px; }
.progress__bar { flex: 0 1 200px; min-width: 120px; height: 8px; border-radius: 999px; background: #f1f5f9; overflow: hidden; }
.progress__bar span { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #60a5fa, #2563eb); transition: width .3s ease; }
.progress__text { font-size: 12px; color: #475569; white-space: nowrap; }
.progress__text b { color: #1d4ed8; }
.hero__meta { display: flex; flex-wrap: wrap; gap: 16px; font-size: 12px; color: #64748b; }
.hero__meta b { color: #0f172a; font-variant-numeric: tabular-nums; }
.hero__meta em { font-style: normal; color: #94a3b8; }
.hero__meta b.is-up { color: #15803d; }
.hero__meta b.is-down { color: #b45309; }

.metrics { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-auto-rows: 1fr; gap: 10px; }
.metric { display: flex; flex-direction: column; justify-content: center; gap: 4px; padding: 10px 12px; background: #fff; border: 1px solid #e2e8f0; border-left: 3px solid #2563eb; border-radius: 10px; }
.metric--success { border-left-color: #16a34a; }
.metric--warning { border-left-color: #f59e0b; }
.metric--violet { border-left-color: #7c3aed; }
.metric__label { font-size: 12px; color: #64748b; }
.metric__value { font-size: 18px; font-weight: 600; color: #0f172a; font-variant-numeric: tabular-nums; }
.metric__value small { font-size: 12px; font-weight: 400; color: #64748b; }

.panel { padding: 14px 16px; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; }
.panel__head { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
.panel__title { font-size: 14px; font-weight: 600; color: #0f172a; }
.panel__empty { padding: 24px 0; text-align: center; color: #94a3b8; font-size: 13px; }
.chart-summary { display: inline-flex; align-items: center; gap: 12px; color: #64748b; font-size: 12px; }
.chart-summary b { color: #2563eb; font-size: 13px; font-variant-numeric: tabular-nums; }

.chart { display: block; width: 100%; height: 190px; overflow: visible; }
.chart__line { fill: none; stroke: #2563eb; stroke-width: 2.5; stroke-linecap: round; stroke-linejoin: round; vector-effect: non-scaling-stroke; }
.chart__area { fill: url(#pet-score-area); }
.chart__grid { stroke: #eef2f7; stroke-width: 1; vector-effect: non-scaling-stroke; }
.chart__axis-text { fill: #94a3b8; font-size: 10px; }
.chart__point { fill: #fff; stroke: #2563eb; stroke-width: 2; vector-effect: non-scaling-stroke; cursor: pointer; transition: r .15s ease, fill .15s ease; }
.chart__point:hover { r: 6; fill: #dbeafe; }
.chart__level { stroke: #bfdbfe; stroke-dasharray: 4 4; opacity: .7; vector-effect: non-scaling-stroke; }

.legend { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #64748b; }
.legend__dot { display: inline-block; width: 10px; height: 10px; border-radius: 3px; margin: 0 2px 0 8px; }
.months { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
.month__title { font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 6px; }
.month__week, .month__grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; }
.month__week span { text-align: center; font-size: 11px; color: #94a3b8; }
.month__grid { margin-top: 3px; }
.cell { height: 24px; display: flex; align-items: center; justify-content: center; border-radius: 4px; font-size: 11px; font-variant-numeric: tabular-nums; cursor: pointer; transition: transform .15s ease, box-shadow .15s ease; }
.cell:hover { transform: translateY(-1px); box-shadow: 0 2px 6px rgba(15, 23, 42, .16); }
.cell--blank { background: transparent; }
.cell--none { background: #f1f5f9; color: #94a3b8; }
.cell--stable { background: #dbeafe; color: #1d4ed8; }
.cell--energetic { background: #bbf7d0; color: #15803d; }
.cell--adjusting { background: #fde68a; color: #b45309; }

.logs { padding: 4px 0 0 4px; }
.log { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 13px; }
.log__type { padding: 1px 8px; border-radius: 999px; font-size: 12px; font-weight: 500; }
.log__type.is-claim { background: #dcfce7; color: #15803d; }
.log__type.is-change { background: #eff6ff; color: #1d4ed8; }
.log__pet { color: #475569; }
.log__pet--to { color: #0f172a; font-weight: 500; }
.muted { color: #64748b; font-size: 12px; }
.mood-tag { display: inline-block; padding: 1px 8px; border-radius: 999px; font-size: 12px; font-weight: 500; }
.mood-stable { background: #eff6ff; color: #1d4ed8; }
.mood-energetic { background: #dcfce7; color: #15803d; }
.mood-adjusting { background: #fef3c7; color: #b45309; }
.day-detail { color: #334155; }
.day-detail__date { font-size: 20px; font-weight: 650; color: #0f172a; }
.day-detail__status { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
.day-detail__grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-top: 18px; }
.day-detail__grid > div { display: flex; flex-direction: column; gap: 5px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; }
.day-detail__grid span { color: #64748b; font-size: 12px; }
.day-detail__grid b { color: #0f172a; font-size: 16px; font-variant-numeric: tabular-nums; }
.day-detail__grid b.is-up { color: #15803d; }
.day-detail__grid b.is-down { color: #b45309; }
</style>
